import { useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import {
  useLazyGetRentalFeesByConfiguredServiceQuery,
  useLazyGetRentalFeesResetHistoryQuery,
} from 'api/configured-service/configuredServiceRentalFeesCrud'
import { useLazyGetServiceDetailsQuery } from 'api/configured-service/getServiceDetails'
import { useLazyGetRentalFeesQuery } from 'api/pricing/rentalFeesCrud'
import { useLazyFetchWorkOrderListQuery } from 'api/work-order/fetchWorkOrderList'
import { RECURRENCE } from 'settings/constants/service'
import { setRentalFeeDialogState, resetRentalFeeDialogState, selectSelectedDay } from 'slices/pricing/rentalFeeDialogSlice'
import { createDateFromString, formatDateToBEFormatDateFns } from 'utils/date'
import { handleError } from 'utils/error'
import { get } from 'utils/lodash'
import { formatCentsToDollars } from 'utils/price'

import {
  DEFAULT_RENTAL_FEE,
  getRentalPeriodDateRange,
  getLatestRentalPeriod,
  buildEventsByDate,
  filterWorkOrdersByDate,
  getRentalFeeResetCount,
  isClosedPeriod,
  isOngoingPeriod,
  filterValidRentalPeriods,
} from './rentalPeriodUtils'

const { RECURRING } = RECURRENCE

/**
 * Data orchestrator hook for rental fee feature.
 * Creates the form, fetches data, computes derived state, and dispatches everything to the Redux slice.
 * Handlers are provided by useRentalFeeActions (called inside FormProvider).
 *
 * @param {Object} options
 * @param {string} options.configuredServiceId - The configured service ID
 * @param {string} [options.accountId] - The account ID
 * @param {boolean} [options.enabled] - Whether to fetch data (default: true)
 * @param {boolean} [options.isDialog] - Whether to fetch service details and rental fee dropdown (default: false)
 * @param {boolean} [options.readOnly] - Whether the view is read-only (default: false)
 * @returns {Object} form, handleSubmit, isGetLoading
 */
const useRentalFeeData = ({ configuredServiceId, accountId: externalAccountId, enabled = true, isDialog = false, readOnly = false }) => {
  const dispatch = useDispatch()

  const [
    getRentalFeesByConfiguredService,
    {
      isFetching: isGetRentalFeesByConfiguredServiceLoading,
      data: configuredRentalFeesData,
      isSuccess: isRentalFeesQuerySuccess,
      originalArgs: rentalFeesQueryArgs,
    },
  ] = useLazyGetRentalFeesByConfiguredServiceQuery()

  const isRentalFeesDataCurrent = rentalFeesQueryArgs?.id === configuredServiceId
  const isSuccess = isRentalFeesQuerySuccess && isRentalFeesDataCurrent
  const [fetchWorkOrderList, { data: workOrdersData }] = useLazyFetchWorkOrderListQuery()
  const [getRentalFeesResetHistory, { data: resetHistoryData }] = useLazyGetRentalFeesResetHistoryQuery()

  const [getServiceDetails, { isFetching: isGetServiceDetailsLoading, data: csData }] = useLazyGetServiceDetailsQuery()
  const [getRentalFees, { isFetching: isGetRentalFeesLoading }] = useLazyGetRentalFeesQuery()

  const accountId = useMemo(() => externalAccountId || get(csData, 'accountId', ''), [externalAccountId, csData])

  const existingRentalFee = useMemo(() => {
    if (!isRentalFeesDataCurrent) return null
    const configuredRentalFees = get(configuredRentalFeesData, 'configuredRentalFees', [])
    return configuredRentalFees.length > 0 ? configuredRentalFees[0] : null
  }, [configuredRentalFeesData, isRentalFeesDataCurrent])

  const rentalFeeResetCount = useMemo(() => getRentalFeeResetCount(resetHistoryData), [resetHistoryData])

  const { earliestStartDate: rentalPeriodStartDate, latestEndDate: latestRentalPeriodEndDate } = useMemo(
    () => getRentalPeriodDateRange(existingRentalFee?.rentalPeriods),
    [existingRentalFee]
  )

  const latestRentalPeriod = useMemo(() => getLatestRentalPeriod(existingRentalFee?.rentalPeriods), [existingRentalFee])
  const hasRentalPeriods = Boolean(existingRentalFee?.rentalPeriods?.length)

  const isNewRentalFee = !existingRentalFee
  const isLatestPeriodClosed = isClosedPeriod(latestRentalPeriod)
  const isLatestPeriodOngoing = isOngoingPeriod(latestRentalPeriod)
  const isOngoingRentalFee = isLatestPeriodOngoing

  const latestRentalPeriodStartDate = useMemo(
    () => (latestRentalPeriod.startDate ? createDateFromString(latestRentalPeriod.startDate) : null),
    [latestRentalPeriod.startDate]
  )

  const workOrders = useMemo(() => get(workOrdersData, 'workOrders', []), [workOrdersData])
  const eventsByDate = useMemo(() => buildEventsByDate(workOrders), [workOrders])

  const isStartDateDeletable = !hasRentalPeriods || !isOngoingRentalFee

  const { serviceType, serviceName, price, containerCount, serviceStartDateBEFormat } = useMemo(
    () => ({
      serviceType: get(csData, 'priceServiceDetail.serviceType', ''),
      serviceName: get(csData, 'priceServiceDetail.serviceName', ''),
      price: get(csData, 'price', 0),
      containerCount: get(csData, 'advanced.newContainerCount', 0),
      serviceStartDateBEFormat: get(csData, 'schedule.startDate', ''),
    }),
    [csData]
  )

  const isRecurringService = serviceType === RECURRING
  const serviceStartDate = useMemo(
    () => (isRecurringService && serviceStartDateBEFormat ? createDateFromString(serviceStartDateBEFormat) : new Date()),
    [isRecurringService, serviceStartDateBEFormat]
  )

  const prevEnabledRef = useRef(enabled)
  const needsReduxSync = enabled && !prevEnabledRef.current

  const hasStaleRentalFeesData = enabled && !isRentalFeesDataCurrent
  const isGetLoading =
    isGetServiceDetailsLoading ||
    isGetRentalFeesLoading ||
    (isGetRentalFeesByConfiguredServiceLoading && !configuredRentalFeesData) ||
    hasStaleRentalFeesData ||
    needsReduxSync
  const hasRentalFee = Boolean(existingRentalFee)
  const noRentalFees = !existingRentalFee && isSuccess

  const form = useForm({ defaultValues: { rentalFee: DEFAULT_RENTAL_FEE } })
  const { reset, handleSubmit } = form

  const serviceInfo = useMemo(
    () => ({
      configuredServiceId,
      readOnly,
      serviceName,
      price,
      containerCount,
      isRecurringService,
      serviceStartDate: serviceStartDate ? serviceStartDate.toISOString() : null,
    }),
    [configuredServiceId, readOnly, serviceName, price, containerCount, isRecurringService, serviceStartDate]
  )

  const rentalFeeStatus = useMemo(
    () => ({
      rentalFeeResetCount,
      existingRentalFee,
      hasRentalFee,
      noRentalFees,
      isNewRentalFee,
      isLatestPeriodClosed,
      isLatestPeriodOngoing,
      isOngoingRentalFee,
      isStartDateDeletable,
      latestRentalPeriodStartDate: latestRentalPeriodStartDate ? latestRentalPeriodStartDate.toISOString() : null,
    }),
    [
      rentalFeeResetCount,
      existingRentalFee,
      hasRentalFee,
      noRentalFees,
      isNewRentalFee,
      isLatestPeriodClosed,
      isLatestPeriodOngoing,
      isOngoingRentalFee,
      isStartDateDeletable,
      latestRentalPeriodStartDate,
    ]
  )

  useEffect(() => {
    prevEnabledRef.current = enabled
    if (!enabled) return

    dispatch(
      setRentalFeeDialogState({
        ...serviceInfo,
        ...rentalFeeStatus,
        eventsByDate,
        isGetLoading,
      })
    )
  }, [enabled, dispatch, serviceInfo, rentalFeeStatus, eventsByDate, isGetLoading])

  useEffect(() => {
    if (!enabled) {
      reset({ rentalFee: DEFAULT_RENTAL_FEE })
      dispatch(resetRentalFeeDialogState())
    }
  }, [enabled, dispatch, reset])

  useEffect(() => {
    return () => dispatch(resetRentalFeeDialogState())
  }, [dispatch])

  useEffect(() => {
    if (enabled && configuredServiceId) {
      getRentalFeesByConfiguredService({ id: configuredServiceId }).unwrap().catch(handleError)
      getRentalFeesResetHistory({ id: configuredServiceId }).unwrap().catch(handleError)
    }
  }, [enabled, configuredServiceId, getRentalFeesByConfiguredService, getRentalFeesResetHistory])

  useEffect(() => {
    if (isDialog && enabled && configuredServiceId) {
      getRentalFees().unwrap().catch(handleError)
      getServiceDetails(configuredServiceId).unwrap().catch(handleError)
    }
  }, [isDialog, enabled, configuredServiceId, getRentalFees, getServiceDetails])

  useEffect(() => {
    if (!accountId || !rentalPeriodStartDate) return
    const endDate = latestRentalPeriodEndDate || formatDateToBEFormatDateFns(new Date())
    fetchWorkOrderList({
      accountId,
      configuredServiceIds: [configuredServiceId],
      startDate: rentalPeriodStartDate,
      endDate,
    })
      .unwrap()
      .catch(handleError)
  }, [accountId, rentalPeriodStartDate, latestRentalPeriodEndDate, configuredServiceId, fetchWorkOrderList])

  useEffect(() => {
    if (!enabled || !isSuccess) return

    if (existingRentalFee) {
      reset({
        rentalFee: {
          rentalFeeToConfiguredServiceId: existingRentalFee.id,
          rentalFeeId: existingRentalFee.rentalFeeId,
          feeName: existingRentalFee.feeName,
          pricingPeriod: existingRentalFee.pricingPeriod,
          gracePeriodDays: existingRentalFee.gracePeriodDays,
          startDate: null,
          endDate: null,
          amount: formatCentsToDollars(existingRentalFee.amountCents),
          billingData: {
            unbilledPeriods: existingRentalFee.unbilledPeriods || [],
            billedPeriods: existingRentalFee.billedPeriods || [],
            gracePeriods: existingRentalFee.gracePeriods || [],
          },
          rentalPeriods: filterValidRentalPeriods(existingRentalFee.rentalPeriods || []),
          activePeriodId: null,
        },
      })
    } else {
      reset({ rentalFee: DEFAULT_RENTAL_FEE })
    }
  }, [enabled, isSuccess, existingRentalFee, reset])

  const selectedDayISO = useSelector(selectSelectedDay)
  const selectedDayWorkOrders = useMemo(() => {
    if (!selectedDayISO) return []
    return filterWorkOrdersByDate(new Date(selectedDayISO), workOrders)
  }, [selectedDayISO, workOrders])

  useEffect(() => {
    dispatch(setRentalFeeDialogState({ selectedDayWorkOrders }))
  }, [dispatch, selectedDayWorkOrders])

  return {
    form,
    handleSubmit,
    isGetLoading,
  }
}

export default useRentalFeeData
